import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { FaBars, FaTimes } from 'react-icons/fa';

const Nav = styled.nav`
  background: #000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 9999;
  height: 60px;
`;

const NavContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const LogoContainer = styled.div`
  flex: 1;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  text-decoration: none;
  z-index: 10000;

  img {
    height: 40px;
    width: auto;
  }
`;

const NavMenu = styled.ul`
  display: none;
  list-style: none;
  margin: 0;
  padding: 0;

  @media screen and (min-width: 769px) {
    display: flex;
    gap: 20px;
  }
`;

const NavItem = styled.li`
  margin: 0;
  padding: 0;
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 10px;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 10px;
  z-index: 10000;

  @media screen and (min-width: 769px) {
    display: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media screen and (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 60px;
    right: 0;
    width: 280px;
    height: calc(100vh - 60px);
    background: ${({ theme }) => theme.colors.background.dark};
    padding: 0;
    flex-direction: column;
    box-shadow: -2px 4px 6px rgba(0, 0, 0, 0.1);
    transform-origin: top right;
    transition: all 0.3s ease-in-out;
    transform: ${({ $isOpen }) => $isOpen ? 'scaleX(1)' : 'scaleX(0)'};
    opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
    z-index: 9999;
    overflow-y: auto;
  }
`;

const MenuItem = styled.div`
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary.main}20;
`;

const MenuButton = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: left;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary.main}20;
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary.main}40;
  }
`;

const SubMenuButton = styled(MenuButton)`
  padding-left: 40px;
  font-size: 1rem;
  
  &:hover {
    padding-left: 45px;
  }
`;

const MenuTrigger = styled.button<{ $isOpen?: boolean; theme: any }>`
  width: 100%;
  background: none;
  border: none;
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary.main}20;
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary.main}40;
  }

  &::after {
    content: '›';
    font-size: 1.5rem;
    transition: transform 0.3s ease;
  }

  ${({ $isOpen, theme }) => $isOpen && `
    background: ${theme.colors.primary.main}20;
    &::after {
      transform: rotate(90deg);
    }
  `}
`;

const MenuLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: 1.1rem;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary.main}20;
    padding-left: 25px;
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary.main}40;
  }
`;

const SubMenu = styled.div<{ $isOpen: boolean }>`
  max-height: ${({ $isOpen }) => ($isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: ${({ theme }) => theme.colors.background.darker};
`;

const SubMenuLink = styled(MenuLink)`
  padding-left: 40px;
  font-size: 1rem;
  
  &:hover {
    padding-left: 45px;
  }
`;

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleMobileNavigation = (path: string, anchor?: string) => {
    setIsOpen(false);
    setActiveSubmenu(null);

    if (anchor) {
      if (location.pathname === path) {
        // Se já estiver na página correta, apenas faz o scroll
        const element = document.getElementById(anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Se estiver em outra página, navega e depois faz o scroll
        navigate(path);
        setTimeout(() => {
          const element = document.getElementById(anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } else {
      navigate(path);
    }
  };

  const toggleSubmenu = (menu: string) => {
    setActiveSubmenu(activeSubmenu === menu ? null : menu);
  };

  return (
    <Nav role="navigation" aria-label="Menu principal">
      <NavContainer>
        <LogoContainer>
          <Logo to="/" aria-label="Ir para página inicial">
            <img 
              alt="Logo Fagundes Tech" 
              src="/fagundes.tech-white.svg"
              width="150"
              height="40"
            />
          </Logo>
        </LogoContainer>

        <NavMenu role="menubar">
          <NavItem>
            <NavLink to="/">Home</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/about">Sobre</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/portfolio">Portfólio</NavLink>
          </NavItem>
          <NavItem>
            <NavLink to="/contact">Contato</NavLink>
          </NavItem>
        </NavMenu>

        <HamburgerButton 
          onClick={() => setIsOpen(!isOpen)} 
          aria-label="Menu"
          aria-expanded={isOpen}
        >
          {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </HamburgerButton>

        <MobileMenu 
          $isOpen={isOpen} 
          role="menu" 
          aria-hidden={!isOpen}
          aria-label="Menu mobile"
        >
          <MenuItem>
            <MenuButton onClick={() => handleMobileNavigation('/')}>
              Home
            </MenuButton>
          </MenuItem>

          <MenuItem>
            <MenuTrigger 
              onClick={() => toggleSubmenu('about')}
              $isOpen={activeSubmenu === 'about'}
            >
              Sobre
            </MenuTrigger>
            <SubMenu $isOpen={activeSubmenu === 'about'}>
              <SubMenuButton 
                onClick={() => handleMobileNavigation('/about', 'about')}
              >
                Quem Somos
              </SubMenuButton>
              <SubMenuButton 
                onClick={() => handleMobileNavigation('/about', 'team')}
              >
                Nossa Equipe
              </SubMenuButton>
              <SubMenuButton 
                onClick={() => handleMobileNavigation('/about', 'tech')}
              >
                Tecnologias
              </SubMenuButton>
            </SubMenu>
          </MenuItem>

          <MenuItem>
            <MenuTrigger 
              onClick={() => toggleSubmenu('portfolio')} 
              $isOpen={activeSubmenu === 'portfolio'}
            >
              Portfólio
            </MenuTrigger>
            <SubMenu $isOpen={activeSubmenu === 'portfolio'}>
              <SubMenuButton onClick={() => handleMobileNavigation('/portfolio')}>
                Todos os Projetos
              </SubMenuButton>
              <SubMenuButton onClick={() => handleMobileNavigation('/portfolio', 'web')}>
                Projetos Web
              </SubMenuButton>
              <SubMenuButton onClick={() => handleMobileNavigation('/portfolio', 'mobile')}>
                Projetos Mobile
              </SubMenuButton>
              <SubMenuButton onClick={() => handleMobileNavigation('/portfolio', 'cloud')}>
                Projetos Cloud
              </SubMenuButton>
            </SubMenu>
          </MenuItem>

          <MenuItem>
            <MenuButton onClick={() => handleMobileNavigation('/contact')}>
              Contato
            </MenuButton>
          </MenuItem>
        </MobileMenu>
      </NavContainer>
    </Nav>
  );
};